/* components/ui/Num.jsx — единый кегль числовых значений, подобранный ТОЧНО.

   Задача: все крупные числа группы одного размера, а размер — максимальный из тех,
   при которых КАЖДОЕ число ещё помещается в свою карточку в одну строку.

   Почему считаем, а не подбираем пороги. Первая версия вешала кегль по лестнице
   «длина строки → px», а пороги были подобраны под «самую узкую карточку ≈200px».
   На мобиле трек `minmax(148px, 1fr)`: доступно ≈124px, а «5 998 739,87 ₽» при 15px
   просит ≈126px — и символ валюты уезжал на вторую строку. Это не промах в тюнинге,
   а захардкоженное допущение о вёрстке, которое ломается молча при любой правке сетки
   и при смене валюты (AED шире ₽ — у Syrve-подключений именно он).

   Считаем от ФАКТИЧЕСКОГО рендера, а не от модели ширины. Две попытки смоделировать
   провалились на живых данных, и обе давали кегль, при котором число не влезало:
     • `canvas.measureText` не знает про font-variant-numeric;
     • скрытый спан со скопированным начертанием дал ширину на 6% меньше реальной
       (8.46 против 8.95 — шрифт Geist Mono, letter-spacing 0.42px).
   Пропорция clientWidth/scrollWidth берётся из самой реальности и не зависит от того,
   что мы думаем про шрифт. Ширина при этом АФФИННА по кеглю (фиксированные
   межбуквенные интервалы не масштабируются), поэтому один проход приближает, а
   сходится за два — досчёт планируется сам, не более MAX_PASSES раз.

   ⚠️ Условие устойчивости: ширина коробки НЕ должна зависеть от содержимого. У нас
   треки объявлены `minmax(0, 1fr)` / `minmax(148px, 1fr)` — смена кегля не может
   изменить ширину, обратной связи нет. Для контейнеров, ужимающихся по содержимому
   (`width: fit-content`, `display: inline-block`), механизм зациклится — не ставить.

   Ниже пола читаемости кегль не опускаем: вместо этого разрешаем перенос, чтобы
   значение осталось ТОЧНЫМ. Сокращать «5 998 739,87 ₽» до «6,0 млн ₽» ради вёрстки —
   продуктовое решение, а не типографское: это финансовый отчёт, и цифра в нём должна
   быть та, что в кассе. Понадобится — включим осознанно, отдельным пропом.

   Использование:
     <NumScale className="analytics-body">      // ОДИН на страницу
       <Num className="ov-kpi__value">{val}</Num>
     </NumScale>

   Масштаб ОДИН НА СТРАНИЦУ — требование прямое: «все цифры одного размера». Пробовали
   по группам (свой масштаб на каждую сетку карточек) — числа в ряду выравнивались, но
   ряды между собой снова разъезжались, то есть возвращалась ровно исходная жалоба.
   NumScale вложенный, ближайший выигрывает: если когда-нибудь решим, что широкие
   карточки не должны мельчать из-за узких, достаточно обернуть секцию своим. */
import React from 'react';

const { useState, useCallback, useRef, useLayoutEffect, useEffect, useContext, useMemo, createContext } = React;

export const MIN_PX = 14;    // пол читаемости; ниже — не мельчим, а разрешаем перенос
export const MAX_PX = 21;
const MAX_PASSES = 3;        // сходится за 2; третий — запас на дробные округления

/** Кегль по ФАКТИЧЕСКОМУ рендеру: entries — [{avail, need}], где avail = clientWidth
 *  коробки, need = scrollWidth строки при ТЕКУЩЕМ кегле `current`.
 *
 *  Считаем от того, что браузер уже нарисовал, а не от модели ширины. Две попытки
 *  смоделировать провалились на живых данных: `canvas.measureText` не знает про
 *  font-variant-numeric, а скрытый спан со скопированным начертанием дал ширину на
 *  6% меньше реальной (8.46 против 8.95 — шрифт Geist Mono, letter-spacing 0.42px;
 *  воспроизвести рендер в отдельном элементе надёжно не вышло). Пропорция avail/need
 *  берётся из самой реальности и не зависит от того, что мы думаем про шрифт.
 *
 *  Ширина АФФИННА по кеглю (фиксированные межбуквенные интервалы не масштабируются),
 *  поэтому один шаг лишь приближает — второй проход планирует вызывающий. */
export function fitSize(entries, { min = MIN_PX, max = MAX_PX, current = max } = {}) {
  let ratio = Infinity;
  let measured = false;
  for (const e of entries) {
    if (!e || !e.avail || !e.need) continue;    // ещё не в лэйауте — пропускаем
    measured = true;
    const r = e.avail / e.need;
    if (r < ratio) ratio = r;
  }
  if (!measured) return { px: max, wrap: false };
  const px = Math.floor(current * ratio);
  return { px: Math.max(min, Math.min(max, px)), wrap: px < min };
}

const NumScaleCtx = createContext(null);

export function NumScale({ children, className = '', as: Tag = 'div',
  min = MIN_PX, max = MAX_PX }) {
  const rootRef = useRef(null);
  const nodes = useRef(new Map());          // id → {el, text}
  const raf = useRef(0);
  const passes = useRef(0);          // предохранитель от бесконечного досчёта
  const schedRef = useRef(() => {});
  const [fit, setFit] = useState({ px: max, wrap: false });

  const measure = useCallback(() => {
    raf.current = 0;
    // Только ЧТЕНИЕ: ничего в DOM не пишем, поэтому лэйаут пересчитывается один раз
    // на весь замер, а не на каждое число.
    const entries = [];
    let current = 0;
    nodes.current.forEach(({ el, text }) => {
      if (!el || !el.isConnected || !text) return;
      if (!current) current = parseFloat(getComputedStyle(el).fontSize) || max;
      entries.push({ avail: el.clientWidth, need: el.scrollWidth });
    });
    const next = fitSize(entries, { min, max, current: current || max });
    setFit((prev) => {
      if (prev.px === next.px && prev.wrap === next.wrap) return prev;
      // ширина аффинна по кеглю — первый шаг приближает, второй добивает точно
      if (passes.current < MAX_PASSES) { passes.current += 1; schedRef.current(); }
      return next;
    });
  }, [min, max]);

  const schedule = useCallback(() => {
    if (raf.current) return;
    raf.current = (typeof requestAnimationFrame === 'function')
      ? requestAnimationFrame(measure) : setTimeout(measure, 0);
  }, [measure]);

  const register = useCallback((id, el, text) => {
    passes.current = 0;              // входные данные изменились — досчёт заново
    const prev = nodes.current.get(id);
    if (prev && prev.el && ro.current) {
      ro.current.unobserve(prev.el);
      widths.current.delete(prev.el);
    }
    if (el == null) {
      nodes.current.delete(id);
    } else {
      nodes.current.set(id, { el, text });
      if (ro.current) ro.current.observe(el);
    }
    schedule();
  }, [schedule]);

  /* Следим за шириной КАЖДОГО числа, а не только корня.
     Наблюдения за корнем не хватало: элемент, смонтированный до раскладки своего
     блока, отдавал clientWidth = 0, выпадал из замера — а когда блок раскладывался,
     у корня менялась ВЫСОТА, которую мы намеренно игнорируем. Пересчёт не запускался,
     и кегль оставался прежним: на «Обзоре» это давало 21px при доступных 184px под
     строку в 188px (поймано живым прогоном).
     Ширина самого числа зависит только от контейнера, но не от кегля (блочный элемент
     занимает ширину карточки), поэтому обратной связи здесь нет. Высоту по-прежнему
     игнорируем — вот она от кегля зависит и замкнула бы цикл. */
  const widths = useRef(new Map());
  const ro = useRef(null);
  if (ro.current == null && typeof ResizeObserver !== 'undefined') {
    ro.current = new ResizeObserver((recs) => {
      let changed = false;
      for (const rec of recs) {
        const w = rec.contentRect ? rec.contentRect.width : 0;
        const prev = widths.current.get(rec.target);
        if (prev == null || Math.abs(w - prev) >= 0.5) {
          widths.current.set(rec.target, w);
          changed = true;
        }
      }
      if (changed) { passes.current = 0; schedule(); }
    });
  }
  useEffect(() => {
    const obs = ro.current;
    return () => { if (obs) obs.disconnect(); };
  }, []);

  // Веб-шрифт мог доехать после первого замера — тогда ширины были от фолбэка.
  useEffect(() => {
    if (typeof document === 'undefined' || !document.fonts || !document.fonts.ready) return;
    let alive = true;
    document.fonts.ready.then(() => { if (alive) schedule(); });
    return () => { alive = false; };
  }, [schedule]);

  useEffect(() => () => {
    if (raf.current && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf.current);
  }, []);

  schedRef.current = schedule;
  const ctx = useMemo(() => ({ register }), [register]);

  return (
    <NumScaleCtx.Provider value={ctx}>
      <Tag ref={rootRef} className={className}
        style={{ '--num-scale': fit.px + 'px', '--num-wrap': fit.wrap ? 'normal' : 'nowrap' }}>
        {children}
      </Tag>
    </NumScaleCtx.Provider>
  );
}

let seq = 0;

/** Крупное числовое значение. Сообщает свой узел и текст в NumScale — тот подбирает
 *  общий кегль. Не-строковые дети (пилюли, «10 позиций» со словом) в подбор не идут:
 *  иначе слово в разметке ужимало бы все числа группы. */
export function Num({ children, className = '', as: Tag = 'div', measure, scale = true, ...rest }) {
  const ctx = useContext(NumScaleCtx);
  const ref = useRef(null);
  const id = useRef(null);
  if (id.current == null) { seq += 1; id.current = 'n' + seq; }
  /* `measure` — явная строка для подбора кегля, когда дети НЕ простой текст.
     Без неё составное значение молча выпадало из замера: экраны Аналитики рисуют
     сумму как «число + отдельный элемент знака валюты», дети становятся массивом,
     и правило «не-строковые дети в подбор не идут» выкидывало из NumScale ровно
     те значения, ради которых он и заведён. Внешне это выглядело исправным —
     число не переносилось, — но не потому, что кегль подобран, а потому что
     строка обрезалась многоточием. В финансовом отчёте усечённая сумма хуже
     мелкой. Строка ДОЛЖНА быть полной, включая знак валюты: замер идёт по
     фактической ширине отрисованного элемента. */
  /* `scale={false}` — «показывай как число, но в подборе кегля НЕ участвуй».
     Нужен там, где размер прибит в CSS и `--num-scale` не читается. Такой узел
     в замере вреден дважды: сам он не уменьшится (кегль у него свой), а базу
     подбора собьёт — NumScale берёт стартовый кегль у ПЕРВОГО зарегистрированного
     узла, и весь скоуп залипает на его размере. Замерено: на вкладке ABC
     страничный кегль сидел на 14px даже при ширине 1740px, где места вдоволь, —
     ровно потому, что первым регистрировалась выручка карточки A с прибитыми
     14px. Это не «упёрлись в пол», а «прицепились не к тому». */
  const text = (!scale) ? null
    : (measure != null ? String(measure)
      : ((typeof children === 'string' || typeof children === 'number')
        ? String(children) : null));

  useLayoutEffect(() => {
    if (!ctx) return undefined;
    const self = id.current;
    ctx.register(self, text == null ? null : ref.current, text);
    return () => ctx.register(self, null, null);
  }, [ctx, text]);

  return (
    <Tag ref={ref} className={'num' + (className ? ' ' + className : '')} {...rest}>
      {children}
    </Tag>
  );
}
